<template>
    <article v-if="isRedacted" class="tk-case-note-card tk-case-note-card--redacted">
        <div class="tk-case-note-card__summary tk-case-note-card__summary--redacted">
            <q-icon class="tk-case-note-card__lock" name="o_lock" />
            <div class="tk-case-note-card__summary-content">
                <div class="tk-case-note-card__summary-main">
                    <strong>{{ formatNoteDate(note.contact_at) }}</strong>
                    <span class="tk-case-note-card__time">{{ formatNoteTime(note.contact_at) }}</span>
                    <TkContextBadge
                        kind="confidential"
                        value="confidential"
                        label="Confidential"
                    />
                    <TkContextBadge
                        v-if="note.contact_type"
                        kind="contact"
                        :value="note.contact_type"
                        :label="note.contact_type"
                    />
                </div>
            </div>
        </div>
    </article>
    <details v-else class="tk-case-note-card">
        <summary class="tk-case-note-card__summary">
            <q-icon class="tk-case-note-card__chevron" name="chevron_right" />
            <div class="tk-case-note-card__summary-content">
                <div class="tk-case-note-card__summary-main">
                    <strong>{{ formatNoteDate(note.contact_at) }}</strong>
                    <span class="tk-case-note-card__time">{{ formatNoteTime(note.contact_at) }}</span>
                    <TkContextBadge
                        v-if="note.is_confidential"
                        kind="confidential"
                        value="confidential"
                        label="Confidential"
                    />
                    <TkContextBadge
                        v-if="note.contact_type"
                        kind="contact"
                        :value="note.contact_type"
                        :label="note.contact_type"
                    />
                    <TkContextBadge
                        v-for="wellbeingArea in wellbeingAreas"
                        :key="wellbeingArea.id || wellbeingArea.title"
                        kind="pou"
                        :value="wellbeingArea.title"
                        :label="wellbeingArea.title"
                    />
                    <span v-if="identifiedRisks.length" class="tk-case-note-card__risk">
                        <q-icon name="o_error_outline" />
                        {{ identifiedRisks.length }} {{ identifiedRisks.length === 1 ? "risk" : "risks" }}
                    </span>
                    <q-btn
                        v-if="note.can_edit === true"
                        style="margin-left: auto;"
                        flat
                        dense
                        no-caps
                        color="primary"
                        icon="o_edit"
                        @click="$emit('edit', note)"
                    />
                </div>
                <div class="tk-case-note-card__meta">
                    <span><q-icon name="o_person" />{{ note.staff_name || note.staff_email || "-" }}</span>
                    <span><q-icon name="o_schedule" />{{ formatDuration(note.time_worked_minutes) }}</span>
                </div>
            </div>
        </summary>

        <div class="tk-case-note-card__details">
            <div class="tk-case-note-card__detail-row">
                <span>With whom</span>
                <strong>{{ optionLabels(note.with_whom_options) }}</strong>
            </div>
            <div class="tk-case-note-card__detail-row">
                <span>Location</span>
                <strong>{{ note.contact_location || note.location || "-" }}</strong>
            </div>
            <div class="tk-case-note-card__detail-row">
                <span>Face to face</span>
                <strong>{{ note.face_to_face_engagement ? "Yes" : "No" }}</strong>
            </div>
            <div class="tk-case-note-card__detail-row">
                <span>No show</span>
                <strong>{{ note.no_show ? "Yes" : "No" }}</strong>
            </div>
            <div class="tk-case-note-card__detail-row">
                <span>Cancelled by prison</span>
                <strong>{{ note.cancelled_by_prison ? "Yes" : "No" }}</strong>
            </div>
            <div v-if="note.risk_assessments?.length" class="tk-case-note-card__risks">
                <span class="tk-case-note-card__detail-label">Risk assessment</span>
                <p v-if="hasNoRisksIdentified" class="tk-case-note-card__no-risks">
                    No risks identified
                </p>
                <div v-if="identifiedRisks.length" class="tk-case-note-card__risk-list">
                    <details
                        v-for="risk in identifiedRisks"
                        :key="risk.id"
                        class="tk-case-note-card__risk-item"
                        @click.stop
                        @keydown.stop
                    >
                        <summary class="tk-case-note-card__risk-summary">
                            <strong>{{ risk.label }}</strong>
                            <span class="tk-case-note-card__risk-state">
                                <TkContextBadge
                                    kind="risk"
                                    :value="riskManagementLabel(risk)"
                                    :label="riskManagementLabel(risk)"
                                />
                                <q-icon class="tk-case-note-card__risk-chevron" name="expand_more" />
                            </span>
                        </summary>
                        <div class="tk-case-note-card__risk-details">
                            <div>
                                <span>Description</span>
                                <p>{{ risk.description || "-" }}</p>
                            </div>
                            <div v-if="risk.is_managed === false">
                                <span>How will this risk be managed?</span>
                                <p>{{ risk.management_plan || "-" }}</p>
                            </div>
                        </div>
                    </details>
                </div>
            </div>
            <div v-if="note.additional_staff?.length" class="tk-case-note-card__detail-row">
                <span>Additional Kaimahi</span>
                <strong>{{ additionalStaffNames }}</strong>
            </div>
            <div v-if="note.contact_method" class="tk-case-note-card__detail-row">
                <span>Contact method</span>
                <strong>{{ note.contact_method }}</strong>
            </div>
            <div class="tk-case-note-card__detail-row tk-case-note-card__detail-row--narrative">
                <span>Narrative</span>
                <strong>{{ note.body }}</strong>
            </div>
        </div>
    </details>
</template>

<script>
import TkContextBadge from "@/components/TkClients/TkContextBadge.vue";

const formatDuration = (value) => {
    const minutes = Number(value);
    if (!Number.isFinite(minutes) || minutes < 0) return "-";
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return [hours ? `${hours}h` : "", remainingMinutes ? `${remainingMinutes}m` : ""]
        .filter(Boolean)
        .join(" ") || "0m";
};

export default {
    name: "TkCaseNoteCard",
    components: {
        TkContextBadge,
    },
    emits: ["edit"],
    props: {
        note: {
            type: Object,
            required: true,
        },
    },
    computed: {
        isRedacted() {
            return this.note.is_confidential && this.note.is_authorized === false;
        },
        additionalStaffNames() {
            return this.note.additional_staff
                .map(staff => staff.staff_name || staff.staff_email || "-")
                .join(", ");
        },
        wellbeingAreas() {
            if (this.note.wellbeing_areas?.length) return this.note.wellbeing_areas;
            if (!this.note.wellbeing_title) return [];
            return [{ id: this.note.wellbeing_id, title: this.note.wellbeing_title }];
        },
        identifiedRisks() {
            return (this.note.risk_assessments || [])
                .filter(risk => risk.key !== "no_risks_identified");
        },
        hasNoRisksIdentified() {
            return (this.note.risk_assessments || [])
                .some(risk => risk.key === "no_risks_identified");
        },
    },
    methods: {
        formatDuration,
        formatNoteDate(value) {
            const date = new Date(value);
            if (Number.isNaN(date.getTime())) return value || "-";
            return date.toLocaleDateString("en-NZ", { day: "numeric", month: "short", year: "numeric" });
        },
        formatNoteTime(value) {
            const date = new Date(value);
            if (Number.isNaN(date.getTime())) return "";
            return date.toLocaleTimeString("en-NZ", { hour: "2-digit", minute: "2-digit", hour12: false });
        },
        optionLabels(options) {
            const labels = (options || []).map(option => option.label).filter(Boolean);
            return labels.length ? labels.join(", ") : "-";
        },
        riskManagementLabel(risk) {
            if (risk.is_managed == null) return "Not recorded";
            return risk.is_managed ? "Managed" : "Unmanaged";
        },
    },
};
</script>

<style scoped>
.tk-case-note-card {
    border: 1px solid rgba(47, 62, 70, 0.08);
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.97);
    box-shadow: 0 8px 20px rgba(47, 62, 70, 0.05);
    overflow: hidden;
}

.tk-case-note-card__summary {
    display: grid;
    grid-template-columns: 22px minmax(0, 1fr);
    gap: 8px;
    align-items: center;
    min-height: 66px;
    padding: 10px 16px;
    cursor: pointer;
    list-style: none;
}

.tk-case-note-card__summary::-webkit-details-marker { display: none; }
.tk-case-note-card__summary::marker { content: ""; }
.tk-case-note-card__chevron { color: #69766f; transition: transform 0.16s ease; }
.tk-case-note-card__lock { color: #69766f; }
.tk-case-note-card__summary--redacted { cursor: default; }
.tk-case-note-card--redacted { background: #f8faf8; }
.tk-case-note-card[open] .tk-case-note-card__chevron { transform: rotate(90deg); }
.tk-case-note-card__summary-content { min-width: 0; }
.tk-case-note-card__summary-main, .tk-case-note-card__meta { display: flex; align-items: center; }
.tk-case-note-card__summary-main { flex-wrap: wrap; gap: 7px; }
.tk-case-note-card__summary-main strong { color: #3c4b45; font-size: 0.9rem; font-weight: 550; }
.tk-case-note-card__time { color: #859089; font-size: 0.78rem; }
.tk-case-note-card__risk { padding: 3px 9px; border-radius: 999px; font-size: 0.72rem; font-weight: 550; line-height: 1.2; }
.tk-case-note-card__risk { display: inline-flex; align-items: center; gap: 4px; border: 1px solid #edb3a5; background: #fff0eb; color: #af4935; }
.tk-case-note-card__meta { flex-wrap: wrap; gap: 14px; margin-top: 6px; color: #738078; font-size: 0.76rem; }
.tk-case-note-card__meta span { display: inline-flex; align-items: center; gap: 5px; }
.tk-case-note-card__details { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px 20px; padding: 14px 46px 16px; border-top: 1px solid #e3e7e1; background: #fbfcfa; }
.tk-case-note-card__detail-row { display: grid; gap: 3px; }
.tk-case-note-card__detail-row span { color: #7a857e; font-size: 0.72rem; font-weight: 500; letter-spacing: 0.035em; text-transform: uppercase; }
.tk-case-note-card__detail-row strong { color: #46544e; font-size: 0.86rem; font-weight: 450; white-space: pre-wrap; }
.tk-case-note-card__detail-row--narrative { grid-column: 1 / -1; }
.tk-case-note-card__risks { display: grid; grid-column: 1 / -1; gap: 5px; min-width: 0; }
.tk-case-note-card__detail-label, .tk-case-note-card__risk-details span { color: #7a857e; font-size: 0.72rem; font-weight: 500; letter-spacing: 0.035em; text-transform: uppercase; }
.tk-case-note-card__no-risks { margin: 0; color: #46544e; font-size: 0.86rem; font-weight: 450; }
.tk-case-note-card__risk-list { display: grid; gap: 8px; }
.tk-case-note-card__risk-item { overflow: hidden; border: 1px solid rgba(77, 143, 119, 0.18); border-left: 3px solid #4d8f77; border-radius: 7px; background: rgba(247, 250, 248, 0.9); }
.tk-case-note-card__risk-summary { display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 10px 12px; cursor: pointer; list-style: none; }
.tk-case-note-card__risk-summary::-webkit-details-marker { display: none; }
.tk-case-note-card__risk-summary::marker { content: ""; }
.tk-case-note-card__risk-summary > strong { color: #3c4b45; font-size: 0.86rem; font-weight: 550; line-height: 1.35; }
.tk-case-note-card__risk-state { display: inline-flex; flex: 0 0 auto; align-items: center; gap: 6px; }
.tk-case-note-card__risk-chevron { color: #738078; transition: transform 0.16s ease; }
.tk-case-note-card__risk-item[open] .tk-case-note-card__risk-chevron { transform: rotate(180deg); }
.tk-case-note-card__risk-details { display: grid; gap: 10px; padding: 0 12px 12px; }
.tk-case-note-card__risk-details p { margin: 3px 0 0; color: #46544e; font-size: 0.86rem; font-weight: 450; line-height: 1.4; overflow-wrap: anywhere; white-space: pre-wrap; }
.tk-case-note-card__details > :deep(.q-btn) { justify-self: start; }

@media (max-width: 700px) {
    .tk-case-note-card__details { grid-template-columns: 1fr; padding: 14px 38px; }
    .tk-case-note-card__detail-row--narrative { grid-column: auto; }
}
</style>
